import React, { useEffect, useState } from 'react';
import { Box, Button, Chip, CircularProgress, Grid, Skeleton } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useScenarioHeaderStyles } from './ScenarioHeader.Styles';
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import { CustomTooltip } from '@AMIEWEB/Common/Tooltips';
import { useDispatch, useSelector } from 'react-redux';
import { CustomBackdrop, WhiteTooltip } from '@AMIEWEB/Common';
import { usePromiseTracker } from 'react-promise-tracker';
import {
  getPayPackageStatusChipColor,
  getPayPackageStatusChipTextColor,
  getPayPackageStatusName,
  getStatusTransitionEnabledUpdate,
} from '../PayPackage/helper';
import { selectUser } from 'oidc/user.selectors';
import {
  fetchStatusIcon,
  getPayPackageStatusTooltip,
  getStatusUpdateButtonDisabledTooltip,
} from '../PayPackage/PayPackageHelper';
import { DeleteConfirmationModal } from './DeleteConfirmationModal';
import { IEmailType } from 'app/models/Notification/Notification';
import { notificationDataActions } from 'store/redux-store/notification/notification.redux';
import { selectEmailData } from 'store/redux-store/notification/notification.selector';
import { ShareEmailPopper } from '../PayPackage/ShareEmailPopper';
import { ScenarioHeaderDetails } from './ScenarioHeaderDetails/ScenarioHeaderDetails';
import { DisableAppScroll, EnableAppScroll } from 'app/layout/Layout';
import { PromiseTrackerKeys } from 'app/constants/PromiseTrackerKeys';
import { ShareDropdownIconButton } from './ShareDropdownIcon';
import { useFormContext } from 'react-hook-form';
import { IMarginUpdateResponseErrorTypes } from '../PayPackage/models/AddScenarioModal';
import { MarginToolService } from 'app/services/MarginToolServices/margin-tool-service';
import { createPayloadMarginTool } from '@AMIEWEB/MarginTool/marginToolSavePayload';
import { getFormattedTimeOff } from '../Assignments/RequestedTimeOff/EditCellRender/helper';
import { globalActions } from 'app/ApplicationRoot/Global.redux';
import { placementMarginScenarioActions } from 'store/redux-store/margin-tool/slices/placement/placement-add-edit-scenario/add-edit-placement-scenario.redux';
import {
  selectSelectedPlacementMarginScenario,
  selectPlacementMarginOrderIdModifiedConfirmationStatus,
} from 'store/redux-store/margin-tool/slices/placement/placement-add-edit-scenario/add-edit-placement-scenario.selector';
import { selectPlacementMarginEditTimeOffs } from 'store/redux-store/margin-tool/slices/placement/placement-assignment/placement-assignment.selector';
import { placementMarginToolEmailAction } from 'store/redux-store/margin-tool/slices/placement/placement-margin-tool-email/placement-margin-tool-email.redux';
import {
  PlacementMarginStatusTransitionButtons,
  PlacementMarginStatusTransitionModalType,
  PlacementMarginPayPackageStatus,
} from 'store/redux-store/margin-tool/slices/placement/placement-pay-package-status/placement-pay-package-status.model';
import { placementMarginPayPackageStatusActions } from 'store/redux-store/margin-tool/slices/placement/placement-pay-package-status/placement-pay-package-status.redux';
import { selectPlacementMarginStatusUpdatedEmailModalOpen } from 'store/redux-store/margin-tool/slices/placement/placement-pay-package-status/placement-pay-package-status.selector';

export const ScenarioHeader = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const {
    watch,
    formState: { isValid },
  } = useFormContext();

  const isStatusUpdatedEmailModalOpen = useSelector(selectPlacementMarginStatusUpdatedEmailModalOpen);

  const { userInfo } = useSelector(selectUser);
  const headerData = useSelector(selectSelectedPlacementMarginScenario);
  const orderIdModifiedConfirmationStatus = useSelector(selectPlacementMarginOrderIdModifiedConfirmationStatus);
  const emailData = useSelector(selectEmailData);
  const editTimeOffs = useSelector(selectPlacementMarginEditTimeOffs);
  const { classes } = useScenarioHeaderStyles();
  const [isDeleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [openEmailPopper, setOpenEmailPopper] = useState<boolean>(false);
  const [isActionButtonHovered, setActionButtonHovered] = useState<{
    buttonName?: PlacementMarginStatusTransitionButtons;
    isHovered: boolean;
  }>({ buttonName: null, isHovered: false });

  const [selectedStatusButton, setSelectedStatusButton] = useState<PlacementMarginStatusTransitionButtons | null>(null);

  const { promiseInProgress: isScenarioDataLoading } = usePromiseTracker({
    area: 'get-scenario-navigation-tree-data',
    delay: 0,
  });

  const { promiseInProgress: fetchTemplate } = usePromiseTracker({
    area: PromiseTrackerKeys.placementMarginTool.marginToolTemplate,
    delay: 0,
  });
  const { promiseInProgress: fetchPlacementHeader } = usePromiseTracker({
    area: PromiseTrackerKeys.placementMarginTool.fetchplacementHeader,
    delay: 0,
  });
  const { promiseInProgress: fetchOrderDetails } = usePromiseTracker({
    area: PromiseTrackerKeys.placementMarginTool.fetchOrderDetails,
    delay: 0,
  });
  const { promiseInProgress: fetchPlacementDocs } = usePromiseTracker({
    area: PromiseTrackerKeys.placementMarginTool.fetchPlacementDocs,
    delay: 0,
  });
  const { promiseInProgress: fetchBillRateMod } = usePromiseTracker({
    area: PromiseTrackerKeys.placementMarginTool.fetchBillRateMod,
    delay: 0,
  });
  const { promiseInProgress: marginToolPreview } = usePromiseTracker({
    area: PromiseTrackerKeys.placementMarginTool.marginToolPreview,
    delay: 0,
  });

  const { promiseInProgress: flagVariationInProgress } = usePromiseTracker({
    area: PromiseTrackerKeys.placementMarginTool.peopleSoftCallMarginTool,
    delay: 0,
  });
  const { promiseInProgress: isSavingScenario } = usePromiseTracker({
    area: PromiseTrackerKeys.placementMarginTool.saveScenario,
    delay: 0,
  });
  const { promiseInProgress: isLoadDataScenario } = usePromiseTracker({
    area: PromiseTrackerKeys.placementMarginTool.scenarioNavigationData,
    delay: 0,
  });

  const { promiseInProgress: isScenarioVerification } = usePromiseTracker({
    area: PromiseTrackerKeys.placementMarginTool.validateScenario,
    delay: 0,
  });

  const { promiseInProgress: isCalculatedUpdateInProgress } = usePromiseTracker({
    area: PromiseTrackerKeys.placementMarginTool.updateScnarioCalculatedValues,
    delay: 0,
  });

  const inProgress =
    flagVariationInProgress ||
    isSavingScenario ||
    isLoadDataScenario ||
    isScenarioVerification ||
    isCalculatedUpdateInProgress;

  const loading =
    fetchTemplate ||
    fetchPlacementHeader ||
    fetchOrderDetails ||
    fetchPlacementDocs ||
    fetchBillRateMod ||
    marginToolPreview;

  const scenarioName = headerData?.scenarioName?.trimStart();

  const truncateTitle = (title: string, maxLength: number) => {
    return title?.length > maxLength ? `${title?.substring(0, maxLength)}...` : title;
  };
  const statusId = headerData?.scenarioStatusId;
  const statusTransitionButtonList = getStatusTransitionEnabledUpdate(statusId, userInfo, isValid);

  const handleDelete = () => {
    setDeleteConfirmOpen(false);
    dispatch(
      placementMarginScenarioActions.deleteSinglePlacementMarginScenario({
        scenarioId: headerData?.scenarioId,
        timestamp: headerData?.timestamp,
      }),
    );
  };

  const handleEmailModalOpen = (variant: string) => {
    // ShareEmailPopper does not fire ClickAwayListener for clicks on its menu items; always
    // pair DisableAppScroll/openEmailPopper from openEmailMenu with cleanup here.
    EnableAppScroll();
    setOpenEmailPopper(false);

    if (!emailData?.open && emailData?.minimized) {
      dispatch(
        notificationDataActions.setEmailInteraction({
          open: true,
          minimized: false,
        }),
      );
      dispatch(notificationDataActions.setSnackBarData({ channel: 'email', manual: false, changeWarning: true }));
    } else {
      dispatch(
        placementMarginPayPackageStatusActions.setStatusUpdatedEmailModalOpen({
          openEmailModal: true,
          emailType: IEmailType.Manual,
          emailVariant: variant,
        }),
      );
    }
  };

  const changeStatus = statusButton => {
    if (
      statusButton !== PlacementMarginStatusTransitionButtons.reset &&
      statusButton !== PlacementMarginStatusTransitionButtons.verify
    ) {
      setSelectedStatusButton(statusButton);
      const formData = watch();
      new Promise((resolve, reject) => {
        dispatch(
          placementMarginScenarioActions.postPlacementMarginToolDetailsValidate({
            formData,
            resolve,
            reject,
          }),
        );
      }).then(() => {
        setSelectedStatusButton(null);
        dispatch(
          placementMarginPayPackageStatusActions.setPayPackageStatusTransitionModalDetails({
            openModal: true,
            type:
              statusButton === PlacementMarginStatusTransitionButtons.Approve
                ? PlacementMarginStatusTransitionModalType.Approve
                : statusButton === PlacementMarginStatusTransitionButtons.RequestApproval
                ? PlacementMarginStatusTransitionModalType.RequestApproval
                : statusButton === PlacementMarginStatusTransitionButtons.reset
                ? PlacementMarginStatusTransitionModalType?.Reset
                : statusButton === PlacementMarginStatusTransitionButtons.verify
                ? PlacementMarginStatusTransitionModalType.Verify
                : PlacementMarginStatusTransitionModalType.None,
          }),
        );
      });
    } else {
      dispatch(
        placementMarginPayPackageStatusActions.setPayPackageStatusTransitionModalDetails({
          openModal: true,
          type:
            statusButton === PlacementMarginStatusTransitionButtons.Approve
              ? PlacementMarginStatusTransitionModalType.Approve
              : statusButton === PlacementMarginStatusTransitionButtons.RequestApproval
              ? PlacementMarginStatusTransitionModalType.RequestApproval
              : statusButton === PlacementMarginStatusTransitionButtons.reset
              ? PlacementMarginStatusTransitionModalType?.Reset
              : statusButton === PlacementMarginStatusTransitionButtons.verify
              ? PlacementMarginStatusTransitionModalType.Verify
              : PlacementMarginStatusTransitionModalType.None,
        }),
      );
    }
  };

  const openEmailMenu = async () => {
    const marginToolService = MarginToolService.createInstance();
    const formData = watch();
    const timeOffs = getFormattedTimeOff(editTimeOffs);
    const transformedPayload = createPayloadMarginTool(
      formData,
      headerData,
      userInfo,
      headerData?.timestamp,
      timeOffs,
      true,
    );
    const validationDetails = await marginToolService.postMarginToolValidate(transformedPayload);
    if (validationDetails?.status === 200 && !validationDetails?.data?.inError) {
      DisableAppScroll();
      setOpenEmailPopper(true);
    } else {
      dispatch(
        globalActions.setSnackBar({
          message: validationDetails?.data?.message,
          severity: 'error',
        }),
      );

      if (validationDetails?.data?.errorType === IMarginUpdateResponseErrorTypes.BaseDetailsModifiedError) {
        dispatch(placementMarginScenarioActions.setPlacementMarginIsOrderIdModifiedStatus(true));
        dispatch(placementMarginScenarioActions.setPlacementMarginShowOrderIdModifiedDialogStatus(true));
      }
    }
  };

  useEffect(() => {
    if (isStatusUpdatedEmailModalOpen?.openEmailModal) {
      dispatch(placementMarginToolEmailAction.handlePlacementMarginToolEmailCreation());
    }
  }, [isStatusUpdatedEmailModalOpen?.openEmailModal]);

  useEffect(() => {
    if (headerData?.placementId)
      dispatch(
        placementMarginPayPackageStatusActions.getPlacementMarginEmailRecipientsList({
          placementId: headerData?.placementId,
        }),
      );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [headerData?.placementId]);

  return (
    <>
      <CustomBackdrop open={loading} />
      <Grid item id="marginTool-marginToolDetailsPage-rightContainer-scenario-Header" className={classes.container}>
        <Grid item className={classes.header}>
          <Grid item className={classes.headerLeftSection}>
            <CustomTooltip
              arrow={false}
              hideToolTip={scenarioName?.length <= 15}
              tooltipContent={<span className={classes.headerTooltip}>{scenarioName}</span>}
            >
              <Grid item className={classes.headerTitle}>
                {!isScenarioDataLoading ? (
                  truncateTitle(scenarioName, 15)
                ) : (
                  <Skeleton variant="rectangular" width={150} />
                )}
              </Grid>
            </CustomTooltip>
            {headerData?.scenarioStatusId && (
              <>
                <Grid item>
                  <WhiteTooltip
                    classes={{ tooltip: classes.detailsTooltip, arrow: classes.arrow }}
                    title={getPayPackageStatusTooltip(statusId, headerData)}
                  >
                    <Chip
                      size={'small'}
                      style={{
                        height: '28px',
                        padding: '12px 6px',
                        margin: '3px',
                        color: getPayPackageStatusChipTextColor(statusId),
                        border: `1px solid ${getPayPackageStatusChipTextColor(statusId)}`,
                        backgroundColor: getPayPackageStatusChipColor(statusId),
                      }}
                      label={<span className={classes.statusText}>{getPayPackageStatusName(statusId)}</span>}
                    />
                  </WhiteTooltip>
                </Grid>
                {statusTransitionButtonList?.map(statusButton => {
                  return (
                    <>
                      {statusButton?.show && (
                        <Grid item key={statusButton.button}>
                          <WhiteTooltip
                            title={
                              !statusButton?.enabled &&
                              statusButton.button !== PlacementMarginStatusTransitionButtons.RequestApproval
                                ? getStatusUpdateButtonDisabledTooltip()
                                : ''
                            }
                          >
                            <span>
                              <Button
                                className={classes.statusBtn}
                                disabled={!statusButton?.enabled || !isValid || inProgress}
                                variant="text"
                                startIcon={
                                  isScenarioVerification && selectedStatusButton === statusButton.button ? (
                                    <Box>
                                      <CircularProgress size={16} color="inherit" />
                                    </Box>
                                  ) : (
                                    fetchStatusIcon(
                                      statusButton.button,
                                      statusButton?.enabled,
                                      isActionButtonHovered,
                                      isValid,
                                    )
                                  )
                                }
                                onMouseEnter={() =>
                                  setActionButtonHovered({ buttonName: statusButton.button, isHovered: true })
                                }
                                onMouseLeave={() => setActionButtonHovered({ buttonName: null, isHovered: false })}
                                onClick={() => {
                                  if (orderIdModifiedConfirmationStatus) {
                                    dispatch(
                                      placementMarginScenarioActions.setPlacementMarginShowOrderIdModifiedDialogStatus(
                                        true,
                                      ),
                                    );
                                  } else {
                                    changeStatus(statusButton.button);
                                  }
                                }}
                              >
                                <span className={classes.btnText}>{statusButton?.button}</span>
                              </Button>
                            </span>
                          </WhiteTooltip>
                        </Grid>
                      )}
                    </>
                  );
                })}
              </>
            )}
          </Grid>

          <Grid item className={classes.headerRightSection}>
            <div
              className={classes.shareIcon}
              onClick={() => {
                if (!inProgress) {
                  if (orderIdModifiedConfirmationStatus) {
                    dispatch(placementMarginScenarioActions.setPlacementMarginShowOrderIdModifiedDialogStatus(true));
                  } else {
                    openEmailMenu();
                  }
                }
              }}
            >
              <ShareDropdownIconButton />
            </div>
            <ShareEmailPopper
              openEmailPopper={openEmailPopper}
              handleEmailModalOpen={handleEmailModalOpen}
              setOpenEmailPopper={setOpenEmailPopper}
              disabled={!isValid}
            />

            <CustomTooltip
              tooltipContent={
                statusId === PlacementMarginPayPackageStatus.Pending && headerData?.isMostRecentBookingPeriod
                  ? t('marginTool.header.deleteTooltip')
                  : ''
              }
            >
              <div
                className={`${classes.deleteSection} ${
                  statusId === PlacementMarginPayPackageStatus.Pending && headerData?.isMostRecentBookingPeriod
                    ? ''
                    : classes.disabledButton
                }`}
                onClick={() => {
                  if (orderIdModifiedConfirmationStatus) {
                    dispatch(placementMarginScenarioActions.setPlacementMarginShowOrderIdModifiedDialogStatus(true));
                  } else {
                    setDeleteConfirmOpen(true);
                  }
                }}
              >
                <DeleteOutlinedIcon />
              </div>
            </CustomTooltip>
          </Grid>
        </Grid>
        <ScenarioHeaderDetails />
      </Grid>
      {isDeleteConfirmOpen && (
        <DeleteConfirmationModal
          open={isDeleteConfirmOpen}
          setDialogOpen={setDeleteConfirmOpen}
          handleProceed={handleDelete}
        />
      )}
    </>
  );
};
